import { useEffect } from 'react';
import { normalizeEventPageSize } from '../../common/events/eventCardGridMetrics';
import { getEventOccurrenceKey } from '../../../utils/eventOccurrenceKey';
import {
  FETCH_LIMIT_MULTIPLIER,
  FILTER_TTL_MS,
  PAGE_SIZE_KEY,
  SCROLL_KEY,
  STORAGE_KEY,
  getCookie,
  normalizeSelectionArray,
  setCookie,
} from './artistEventListUtils';

export default function useArtistEventListLifecycle({
  API_URL,
  allFetchedEvents,
  autoRange,
  currentEvents,
  currentPage,
  events,
  fetchAllVenues,
  fetchEvents,
  fetchGenres,
  filterEndDate,
  filterEventName,
  filterGenres,
  filterPrefectures,
  filterRegions,
  filterStartDate,
  filterVenues,
  filterLocally,
  flyerLoadedMapRef,
  flyerSequenceKeyRef,
  flyerUrls,
  generateFlyerUrls,
  itemsPerPage,
  lastFetchLimitRef,
  navAction,
  refreshEventChatUnread,
  restoredOnceRef,
  restoringRef,
  serverMayHaveMoreRef,
  setAutoRange,
  setCurrentEvents,
  setCurrentPage,
  setEvents,
  setFilterEndDate,
  setFilterEventName,
  setFilterGenres,
  setFilterRegions,
  setFilterStartDate,
  setFilterVenues,
  setFlyerLoadedFlags,
  setFlyerUrls,
  setIsCompactCardLayout,
  setIsMobileSearchLayout,
  setItemsPerPage,
  setSortOrder,
  setViewMode,
  sortOrder,
  updatePrefectures,
  viewMode,
}) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 640px)');
    const handleChange = (event) => setIsCompactCardLayout(event.matches);
    setIsCompactCardLayout(query.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange);
      return () => query.removeEventListener('change', handleChange);
    }
    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, [setIsCompactCardLayout]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 760px)');
    const handleChange = (event) => setIsMobileSearchLayout(event.matches);
    setIsMobileSearchLayout(query.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange);
      return () => query.removeEventListener('change', handleChange);
    }
    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, [setIsMobileSearchLayout]);

  useEffect(() => {
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch {}

    const isExpired = (() => {
      const last = Number(saved?.lastTouchedAt);
      if (!Number.isFinite(last) || last <= 0) return false;
      return Date.now() - last > FILTER_TTL_MS;
    })();

    if (isExpired) {
      saved = null;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.setItem(SCROLL_KEY, '0');
      } catch {}
    }

    const savedView = (saved?.viewMode === 'card' || saved?.viewMode === 'list')
      ? saved.viewMode
      : getCookie('artist_event_list_view');
    if (savedView === 'card' || savedView === 'list') setViewMode(savedView);

    restoringRef.current = true;

    let initialStart = '';
    let initialEnd = '';
    let initialSortOrder = 'asc';
    const urlPage = Number(new URLSearchParams(window.location.search || '').get('p'));
    const hasUrlPage = Number.isFinite(urlPage) && urlPage >= 1;

    if (saved) {
      if (saved.sortOrder === 'asc' || saved.sortOrder === 'desc') {
        initialSortOrder = saved.sortOrder;
        setSortOrder(saved.sortOrder);
      } else {
        initialSortOrder = 'asc';
        setSortOrder('asc');
      }
      if (!hasUrlPage && typeof saved.currentPage === 'number' && saved.currentPage >= 1) {
        setCurrentPage(saved.currentPage);
      }
      if (typeof saved.filterStartDate === 'string') { setFilterStartDate(saved.filterStartDate); initialStart = saved.filterStartDate; }
      if (typeof saved.filterEndDate === 'string') { setFilterEndDate(saved.filterEndDate); initialEnd = saved.filterEndDate; }
      if (typeof saved.autoRange === 'string') setAutoRange(saved.autoRange);
      if (typeof saved.filterEventName === 'string') setFilterEventName(saved.filterEventName);
      if (Array.isArray(saved.filterGenres)) setFilterGenres(normalizeSelectionArray(saved.filterGenres));
      else if (typeof saved.filterGenre === 'string' && saved.filterGenre) setFilterGenres(normalizeSelectionArray([saved.filterGenre]));
      if (Array.isArray(saved.filterRegions)) setFilterRegions(normalizeSelectionArray(saved.filterRegions));
      else if (typeof saved.filterRegion === 'string' && saved.filterRegion) setFilterRegions(normalizeSelectionArray([saved.filterRegion]));
      if (Array.isArray(saved.filterPrefectures)) updatePrefectures(normalizeSelectionArray(saved.filterPrefectures));
      else if (typeof saved.filterPrefecture === 'string' && saved.filterPrefecture) updatePrefectures(normalizeSelectionArray([saved.filterPrefecture]));
      if (Array.isArray(saved.filterVenues)) setFilterVenues(normalizeSelectionArray(saved.filterVenues));
      const savedPageSize = normalizeEventPageSize(saved.itemsPerPage, null);
      if (savedPageSize) setItemsPerPage(savedPageSize);
    } else {
      initialSortOrder = 'asc';
      setSortOrder('asc');
      setFilterStartDate('');
      setFilterEndDate('');
      setAutoRange('auto');
      setFilterEventName('');
      setFilterGenres([]);
      setFilterRegions([]);
      updatePrefectures([]);
      setFilterVenues([]);
      setCurrentPage(1);
    }

    (async () => {
      await Promise.all([fetchGenres(), fetchAllVenues()]);
      await fetchEvents({
        startDate: initialStart || undefined,
        endDate: initialEnd || undefined,
        isInitial: true,
        sortOrderOverride: initialSortOrder,
      });

      restoringRef.current = false;
      restoredOnceRef.current = true;

      if (navAction !== 'PUSH') {
        try {
          const y = Number(sessionStorage.getItem(SCROLL_KEY) || '0');
          if (!Number.isNaN(y) && y > 0) {
            setTimeout(() => window.scrollTo(0, y), 0);
          }
        } catch {}
      } else {
        sessionStorage.setItem(SCROLL_KEY, '0');
        window.scrollTo(0, 0);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, navAction]);

  useEffect(() => {
    const newCurrentEvents = events.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    setCurrentEvents(newCurrentEvents);
  }, [currentPage, events, itemsPerPage, setCurrentEvents]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(events.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [events.length, itemsPerPage, currentPage, setCurrentPage]);

  useEffect(() => {
    if (!restoredOnceRef.current) return undefined;
    const timer = setTimeout(() => {
      const start = (filterStartDate || '').trim();
      const end = (filterEndDate || '').trim();
      fetchEvents({
        startDate: start || undefined,
        endDate: end || undefined,
      });
    }, filterEventName.trim() ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEventName, filterGenres, filterPrefectures, filterVenues]);

  useEffect(() => {
    if (!restoredOnceRef.current) return;
    const filtered = filterLocally(allFetchedEvents);
    setEvents(filtered);
    generateFlyerUrls(filtered).then((urls) => setFlyerUrls(urls));
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  useEffect(() => {
    const sequenceKey = `${viewMode}:${(currentEvents || [])
      .map((event, index) => getEventOccurrenceKey(event, index))
      .join('|')}`;
    if (flyerSequenceKeyRef.current !== sequenceKey) {
      flyerSequenceKeyRef.current = sequenceKey;
      flyerLoadedMapRef.current.clear();
    }
    const count = (currentEvents || []).length;
    if (!count) {
      setFlyerLoadedFlags([]);
      return undefined;
    }
    setFlyerLoadedFlags(Array.from({ length: count }, () => false));
    const timers = Array.from({ length: count }, (_, index) =>
      setTimeout(() => {
        setFlyerLoadedFlags((prev) => {
          if (!Array.isArray(prev) || prev.length !== count || prev[index]) return prev;
          const next = [...prev];
          next[index] = true;
          return next;
        });
      }, Math.min(index * 36, 540))
    );
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [currentEvents, flyerLoadedMapRef, flyerSequenceKeyRef, setFlyerLoadedFlags, viewMode]);

  useEffect(() => {
    if (!restoredOnceRef.current) return;
    try {
      const payload = {
        viewMode,
        sortOrder,
        currentPage,
        filterStartDate,
        filterEndDate,
        autoRange,
        filterEventName,
        filterGenres,
        filterRegions,
        filterPrefectures,
        filterVenues,
        itemsPerPage,
        lastTouchedAt: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }, [
    viewMode, sortOrder, currentPage, itemsPerPage,
    filterStartDate, filterEndDate, autoRange,
    filterEventName, filterGenres, filterRegions, filterPrefectures, filterVenues,
    restoredOnceRef,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(itemsPerPage));
    } catch {}
  }, [itemsPerPage]);

  useEffect(() => {
    setCookie('artist_event_list_view', viewMode, 365);
  }, [viewMode]);

  useEffect(() => {
    const onScroll = () => {
      try {
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY || 0));
      } catch {}
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    refreshEventChatUnread();
  }, [refreshEventChatUnread]);

  useEffect(() => {
    if (!restoredOnceRef.current) return;
    const start = (filterStartDate || '').trim();
    const end = (filterEndDate || '').trim();
    const hasServerSearchFilters =
      Boolean(filterEventName.trim()) ||
      filterGenres.length > 0 ||
      filterPrefectures.length > 0 ||
      filterVenues.length > 0;
    const desiredFetchLimit = hasServerSearchFilters
      ? 5000
      : Math.min(itemsPerPage * FETCH_LIMIT_MULTIPLIER, 1000);
    const requiredCount = currentPage * itemsPerPage;
    const needsVisibleItems = events.length < requiredCount && serverMayHaveMoreRef.current;
    const needsLargerWindow = desiredFetchLimit > lastFetchLimitRef.current && serverMayHaveMoreRef.current;
    if (!needsVisibleItems && !needsLargerWindow) return;

    fetchEvents({
      startDate: start || undefined,
      endDate: end || undefined,
      fetchLimitOverride: Math.max(desiredFetchLimit, requiredCount + itemsPerPage * 2),
      preservePage: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, currentPage, events.length]);
}
